
import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';
import { useRouter } from '@/lib/router-compat';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { seedApi, type SeedSize } from '@/lib/api/crm';
import { useSyncStore } from '@/stores/StoreProvider';
import { useSyncRefresh } from '@/contexts/SyncRefreshContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Database, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';
import { Callout } from '@/components/ui/callout';
import { cn } from '@/lib/utils';

function formatLastSynced(iso: string | null): string {
  if (!iso) return 'Never synced';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 60_000) return 'Last synced just now';
  if (diffMs < 3_600_000) {
    const minutes = Math.floor(diffMs / 60_000);
    return `Last synced ${minutes} min ago`;
  }
  return `Last synced ${new Date(iso).toLocaleTimeString()}`;
}

const SEED_PRESETS: Array<{
  size: SeedSize;
  label: string;
  contacts: string;
  counts: string;
}> = [
  {
    size: 'small',
    label: 'Small',
    contacts: '200 contacts',
    counts: '50 deals · 3 projects\n80 tasks',
  },
  {
    size: 'medium',
    label: 'Medium',
    contacts: '2 000 contacts',
    counts: '500 deals · 30 projects\n800 tasks',
  },
  {
    size: 'large',
    label: 'Large',
    contacts: '10 000 contacts',
    counts: '2 500 deals · 150 projects\n4 000 tasks',
  },
];

export const DeveloperPage = observer(function DeveloperPage() {
  usePageTitle('Developer');
  const { currentOrg } = useAuth();
  const router = useRouter();
  const syncStore = useSyncStore();
  const refresh = useSyncRefresh();
  const [seeding, setSeeding] = useState(false);
  const [seedSize, setSeedSize] = useState<SeedSize>('medium');

  let syncLabel = 'Online';
  let syncDotClass = 'bg-green-600';
  switch (syncStore.status) {
    case 'bootstrapping':
    case 'syncing':
      syncLabel = 'Syncing…';
      syncDotClass = 'bg-blue-500 animate-pulse';
      break;
    case 'pushing':
      syncLabel = `Saving ${syncStore.pendingMutationCount} change${syncStore.pendingMutationCount !== 1 ? 's' : ''}…`;
      syncDotClass = 'bg-blue-500';
      break;
    case 'error':
      syncLabel = syncStore.error ?? 'Sync error';
      syncDotClass = 'bg-destructive';
      break;
    case 'offline':
      syncLabel = 'Offline';
      syncDotClass = 'bg-yellow-500';
      break;
  }

  const syncing =
    syncStore.status === 'bootstrapping' ||
    syncStore.status === 'syncing' ||
    syncStore.status === 'pushing';

  async function handleSyncNow() {
    try {
      await refresh();
    } catch (err) {
      console.error('Sync failed:', err);
      toast.error('Sync failed. Check console for details.');
    }
  }

  async function handleSeed() {
    if (seeding || !currentOrg) return;
    setSeeding(true);
    try {
      const result = await seedApi.seed(seedSize);
      toast.success(
        `Seed complete — ${result.companies} companies, ${result.people} people, ${result.dealTypes} deal types, ${result.deals} deals, ${result.projects} projects, ${result.tasks} tasks, ${result.notes} notes`
      );
      router.refresh();
    } catch (err) {
      console.error('Seed failed:', err);
      toast.error('Seed failed. Check console for details.');
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Developer"
        description="Tools for development and testing"
        askAi
        hideTitleOnMobile={false}
      />

      <Card>
        <CardHeader>
          <CardTitle>Synchronization</CardTitle>
          <CardDescription>
            Status of this device's connection to your workspace.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex items-center gap-2 border-t px-6 py-2.5 text-sm">
            <span
              className={cn('h-2 w-2 shrink-0 rounded-full', syncDotClass)}
              aria-hidden="true"
            />
            <span className="font-medium">{syncLabel}</span>
            <span className="ml-auto text-muted-foreground">
              {formatLastSynced(syncStore.lastSyncAt)}
            </span>
          </div>
          <div className="flex items-center justify-between border-t px-6 py-2.5 text-sm">
            <span className="text-muted-foreground">Pending changes</span>
            <span className="font-medium">{syncStore.pendingMutationCount}</span>
          </div>
          <div className="flex items-center justify-between gap-4 border-t px-6 py-3">
            <span className="text-xs text-muted-foreground">
              Sync runs automatically; use this only if something looks stale.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncNow}
              disabled={syncing}
            >
              {syncing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sync now
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Sample data
          </CardTitle>
          <CardDescription>
            Adds sample records to the current workspace. It never deletes existing data — run it again to add another batch.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            {SEED_PRESETS.map((preset) => {
              const selected = seedSize === preset.size;
              return (
                <button
                  key={preset.size}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setSeedSize(preset.size)}
                  className={cn(
                    'rounded-[9px] border px-3 py-2.5 text-left transition-colors',
                    selected
                      ? 'border-primary bg-muted'
                      : 'border-border hover:bg-muted/50'
                  )}
                >
                  <span className={cn('block text-[13.5px]', selected ? 'font-semibold' : 'font-medium')}>
                    {preset.label}
                  </span>
                  <span className="mt-1 block whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
                    {`${preset.contacts}\n${preset.counts}`}
                  </span>
                </button>
              );
            })}
          </div>

          <Callout variant="warning">
            This adds records to the current workspace. Existing data is never deleted.
          </Callout>

          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">
              Medium is recommended for demos.
            </span>
            <Button onClick={handleSeed} disabled={seeding || !currentOrg}>
              {seeding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate sample data'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/settings/developer')({ component: DeveloperPage });
